import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Nav } from 'react-bootstrap';
import showToast from '../../../utils/toast';

const DTHRecharge = () => {
  const [formData, setFormData] = useState({
    dthNumber: '',
    operator: '',
    amount: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [planFilters, setPlanFilters] = useState({
    search: ''
  });
  const [activeCategory, setActiveCategory] = useState('All');

  // Sample DTH operators
  const operators = [
    'Tata Sky',
    'Airtel Digital TV',
    'Dish TV',
    'Sun Direct',
    'Videocon D2H',
    'd2h'
  ];

  // Sample DTH recharge plans
  const rechargePlans = [
    {
      name: 'Value SD Pack',
      price: '249',
      validity: '28 Days',
      channels: '120 SD Channels',
      benefits: 'Free regional add-on + 30 HD complimentary weekend access',
      category: 'SD Pack'
    },
    {
      name: 'Family HD Pack',
      price: '399',
      validity: '30 Days',
      channels: '150 Channels (30 HD)',
      benefits: 'Kids + Sports + Entertainment + Unlimited recording',
      category: 'HD Pack'
    },
    {
      name: 'Sports HD Pack',
      price: '599',
      validity: '60 Days',
      channels: '180 Channels (50 HD)',
      benefits: 'All sports channels + 14-day catch-up TV',
      category: 'Sports'
    },
    {
      name: 'Premium Binge Pack',
      price: '899',
      validity: '90 Days',
      channels: '220 Channels (70 HD)',
      benefits: 'OTT combo + Multi-TV support + Free technician visit',
      category: 'Premium'
    },
    {
      name: 'Annual Platinum Pack',
      price: '3499',
      validity: '365 Days',
      channels: '250 Channels (100 HD)',
      benefits: 'OTT elite bundle + Dolby audio + Priority support',
      category: 'Annual'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'amount') {
      setSelectedPlanName('');
    }
  
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFilterChange = (e) => {
    const { value } = e.target;
    setPlanFilters({
      search: value
    });
  };

  const resetPlanFilters = () => {
    setPlanFilters({
      search: ''
    });
    setActiveCategory('All');
  };

  const handlePlanSelect = (plan) => {
    setFormData(prev => ({
      ...prev,
      amount: plan.price
    }));
    setErrors(prev => ({
      ...prev,
      amount: ''
    }));
    setSelectedPlanName(plan.name);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.dthNumber) {
      newErrors.dthNumber = 'DTH number is required';
    } else if (!/^\d{10,14}$/.test(formData.dthNumber)) {
      newErrors.dthNumber = 'Please enter a valid DTH number (10-14 digits)';
    }

    if (!formData.operator) {
      newErrors.operator = 'Please select an operator';
    }

    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) < 100) {
      newErrors.amount = 'Invalid Amount';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      showToast.success(`DTH Recharge of ₹${formData.amount} initiated for ${formData.dthNumber}`);
      setFormData({
        dthNumber: '',
        operator: '',
        amount: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  const isDthNumberEntered = /^\d{10,14}$/.test(formData.dthNumber);
  const planCategories = ['All', ...new Set(rechargePlans.map(plan => plan.category))];
  const filteredPlans = (() => {
    if (!isDthNumberEntered) {
      return [];
    }

    let plans = [...rechargePlans];

    const amountValue = parseInt(formData.amount, 10);
    if (formData.amount && !Number.isNaN(amountValue)) {
      plans = plans.filter(plan => parseInt(plan.price, 10) >= amountValue);
    }

    if (activeCategory !== 'All') {
      plans = plans.filter(plan => plan.category === activeCategory);
    }

    const query = planFilters.search.trim().toLowerCase();
    if (query) {
      const queryNumber = parseInt(query, 10);
      plans = plans.filter(plan => {
        const searchableText = `${plan.name} ${plan.validity} ${plan.channels} ${plan.benefits}`.toLowerCase();
        const matchesText = searchableText.includes(query);
        const matchesPrice = !Number.isNaN(queryNumber) && parseInt(plan.price, 10) >= queryNumber;
        const matchesPriceExact = plan.price.includes(query);
        return matchesText || matchesPrice || matchesPriceExact;
      });
    }

    return plans;
  })();

  return (
    <Container fluid className="dth-recharge-page p-4">
      {/* Page Header */}
      <marquee
        behavior="scroll"
        direction="left"
        scrollamount="6"
        style={{
          color: '#0f172a',
          fontWeight: 600,
          marginBottom: '0.75rem',
          background: '#ecfeff',
          padding: '0.35rem 0.75rem',
          borderRadius: '8px',
          border: '1px solid #bae6fd',
          fontSize: '13px'
        }}
      >
        HD packs at special prices for limited time! Recharge your DTH today and never miss your favourite shows.
      </marquee>
      <div className="page-header mb-4" style={{ marginTop: 0 }}>
        <h2 className="mb-1" style={{ fontSize: '24px', fontWeight: 600, color: '#2c2268' }}>
          <i className="bi bi-tv me-2"></i>
          DTH Recharge
        </h2>
        <p className="text-muted mb-0">Quick and easy DTH recharge service</p>
      </div>

      <Row>
        {/* Left Side - Recharge Form */}
        <Col lg={5} xl={4} className="mb-4">
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="p-3" style={{ backgroundColor: '#f8f9fa' }}>
              <Form onSubmit={handleSubmit} className="retailer-form">
                <Row>
                  {/* DTH Number */}
                  <Col md={12} className="mb-3">
                    <Form.Group controlId="dthNumber">
                      <div className="input-with-icon">
                        <i className="bi bi-tv input-icon" style={{ fontSize: '16px', color: '#0d6efd' }}></i>
                        <Form.Control
                          type="text"
                          name="dthNumber"
                          placeholder="Enter DTH Number / Customer ID"
                          value={formData.dthNumber}
                          onChange={handleInputChange}
                          maxLength={14}
                          className="ps-5 retailer-input"
                          isInvalid={!!errors.dthNumber}
                        />
                      </div>
                      {errors.dthNumber && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {errors.dthNumber}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Operator */}
                  <Col md={12} className="mb-3">
                    <Form.Group controlId="dthOperator">
                      <div className="input-with-icon">
                        <i className="bi bi-broadcast input-icon" style={{ fontSize: '16px', color: '#0d6efd' }}></i>
                        <Form.Select
                          name="operator"
                          value={formData.operator}
                          onChange={handleInputChange}
                          className="ps-5 retailer-input"
                          isInvalid={!!errors.operator}
                        >
                          <option value="">Select Operator</option>
                          {operators.map((op, index) => (
                            <option key={index} value={op}>{op}</option>
                          ))}
                        </Form.Select>
                      </div>
                      {errors.operator && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {errors.operator}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Amount */}
                  <Col md={12} className="mb-3">
                    <Form.Group controlId="dthAmount">
                      <div className="input-with-icon">
                        <i className="bi bi-currency-rupee input-icon" style={{ fontSize: '16px', color: '#0d6efd' }}></i>
                        <Form.Control
                          type="text"
                          name="amount"
                          placeholder="Enter Amount"
                          value={formData.amount}
                          onChange={handleInputChange}
                          className="ps-5 retailer-input"
                          isInvalid={!!errors.amount}
                        />
                      </div>
                      {errors.amount && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {errors.amount}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Warning Message */}
                  <Col md={12} className="mb-2">
                    <div style={{ 
                      color: '#dc3545', 
                      fontSize: '13px',
                      fontWeight: 500
                    }}>
                      *Recharge once done, cannot be reversed.
                    </div>
                  </Col>

                  {/* Submit Button */}
                  <Col md={12}>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-100"
                      style={{
                        backgroundColor: '#1e1850',
                        border: 'none',
                        padding: '12px 32px',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '15px',
                        boxShadow: '0 4px 6px rgba(30, 24, 80, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#0f0c30';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 12px rgba(30, 24, 80, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#1e1850';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(30, 24, 80, 0.3)';
                      }}
                    >
                      {isSubmitting ? 'Processing...' : 'Proceed'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Side - Recharge Plans */}
        <Col lg={7} xl={8} className="mb-4">
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white border-bottom" style={{ padding: '16px 24px' }}>
              <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c2268' }}>
                <i className="bi bi-card-list me-2"></i>
                Recommended DTH Plans
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              {isDthNumberEntered && (
                <>
                  <div className="plan-filters border-bottom d-flex flex-column flex-lg-row align-items-lg-center gap-3">
                    <Form.Control
                      type="text"
                      name="search"
                      value={planFilters.search}
                      onChange={handleFilterChange}
                      placeholder="Search plans by channels, benefits, validity, or enter amount (e.g., 399)"
                      className="filter-control flex-grow-1 retailer-input"
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={resetPlanFilters}
                    >
                      Clear
                    </Button>
                  </div>
                  <div className="plan-tabs border-bottom">
                    <Nav variant="tabs" activeKey={activeCategory}>
                      {planCategories.map(category => (
                        <Nav.Item key={category}>
                          <Nav.Link
                            eventKey={category}
                            onClick={() => setActiveCategory(category)}
                          >
                            {category}
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                  </div>
                </>
              )}
              {!isDthNumberEntered ? (
                <div className="p-4 text-center text-muted" style={{ fontSize: '14px' }}>
                  Enter a valid DTH number to view recommended plans.
                </div>
              ) : filteredPlans.length === 0 ? (
                <div className="p-4 text-center text-muted" style={{ fontSize: '14px' }}>
                  No plans match the entered amount. Try adjusting the recharge amount.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>
                          Plan
                        </th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>
                          Validity
                        </th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>
                          Channels
                        </th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>
                          Benefits
                        </th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', borderBottom: '2px solid #dee2e6', fontSize: '14px', textAlign: 'right' }}>
                          Price
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPlans.map((plan, index) => {
                        const isSelected = selectedPlanName === plan.name;
                        return (
                        <tr
                          key={index}
                          onClick={() => handlePlanSelect(plan)}
                          style={{
                            transition: 'background-color 0.2s',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#e7f1ff' : 'transparent'
                          }}
                        >
                          <td style={{ padding: '12px 16px', color: '#333', fontSize: '13px', fontWeight: 600 }}>
                            {plan.name}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#666', fontSize: '13px' }}>
                            {plan.validity}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#666', fontSize: '13px' }}>
                            {plan.channels}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#666', fontSize: '13px' }}>
                            {plan.benefits}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#1e1850', fontSize: '13px', fontWeight: 700, textAlign: 'right' }}>
                            ₹{plan.price}
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Custom Styles */}
      <style jsx="true">{`
        .dth-recharge-page {
          background-color: #fff;
          min-height: calc(100vh - 100px);
        }

        .input-with-icon {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
          pointer-events: none;
        }

        .table tbody tr:hover {
          background-color: #f8f9fa;
        }

        .plan-filters {
          padding: 16px 24px;
          background-color: #f9fafc;
        }

        .plan-tabs {
          padding: 0 24px;
          background-color: #ffffff;
        }

        .plan-tabs .nav-link {
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
          color: #495057;
          border: none;
          border-bottom: 2px solid transparent;
        }

        .plan-tabs .nav-link.active {
          color: #1e1850;
          border-bottom-color: #1e1850;
          background-color: rgba(30, 24, 80, 0.08);
          border-radius: 6px 6px 0 0;
        }

        .plan-tabs .nav-link:hover {
          color: #1e1850;
          background-color: rgba(30, 24, 80, 0.06);
        }

        .filter-control {
          font-size: 13px;
        }

        input:focus, select:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.15);
        }

        @media (max-width: 768px) {
          .table-responsive {
            overflow-x: auto;
          }
        }
      `}</style>
    </Container>
  );
};

export default DTHRecharge;

